import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@volca/ui/lib/toast";
import { useApi } from "@/core/api";
import { reportKey } from "@/profitability/query";
import { reportsPath } from "@/profitability/report-query";
import { findingsKey } from "@/profitability/review/query";
import { reportError } from "@/profitability/utils";
import type { TemplateSettings, TemplateSettingsUpdate } from "./types";

export const settingsKey = (businessId: string, reportId: string) => [
  ...reportKey(businessId),
  "settings",
  reportId,
];

/** The template's settings: what its script reads, with the owner's values. */
export const useTemplateSettings = (
  businessId: string,
  reportId: string,
  enabled = true,
) => {
  const api = useApi();
  return useQuery({
    queryKey: settingsKey(businessId, reportId),
    queryFn: ({ signal }) =>
      api
        .get<{
          settings: TemplateSettings;
        }>(`${reportsPath(businessId, reportId)}/settings`, { signal })
        .then((res) => res.data.settings),
    retry: false,
    enabled,
  });
};

/** The rules in a new order; the server puts them back as declared for null. */
const inOrder = (
  rules: TemplateSettings["rules"],
  order: string[] | null | undefined,
) =>
  order && rules
    ? [...rules].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    : rules;

/** What the page shows before the server answers: the change, applied. */
export const applyUpdate = (
  settings: TemplateSettings,
  update: TemplateSettingsUpdate,
): TemplateSettings => ({
  ...settings,
  declared: settings.declared.map((setting) =>
    update.values && setting.key in update.values
      ? { ...setting, value: update.values[setting.key] ?? null }
      : setting,
  ),
  goal_tolerance: { ...settings.goal_tolerance, ...update.goal_tolerance },
  ordered:
    update.rule_order === undefined ? settings.ordered : !!update.rule_order,
  rules: inOrder(settings.rules, update.rule_order)?.map((rule) => ({
    ...rule,
    enabled: update.rules?.[rule.id]?.enabled ?? rule.enabled,
    owner_id:
      update.owners?.rules && rule.id in update.owners.rules
        ? (update.owners.rules[rule.id] ?? null)
        : rule.owner_id,
  })),
  requests: settings.requests?.filter(
    (request) => request.id !== update.dismiss_request,
  ),
  owners: settings.owners && {
    ...settings.owners,
    values: settings.owners.values.map((entry) =>
      update.owners?.values && entry.value in update.owners.values
        ? { ...entry, owner_id: update.owners.values[entry.value] ?? null }
        : entry,
    ),
    rules: Object.fromEntries(
      Object.entries({
        ...settings.owners.rules,
        ...update.owners?.rules,
      }).filter((entry): entry is [string, string] => !!entry[1]),
    ),
  },
});

/**
 * Saves a change optimistically: the page shows it at once, and puts the
 * previous values back with the reason if the server refuses it.
 */
export const useUpdateTemplateSettings = (
  businessId: string,
  reportId: string,
) => {
  const api = useApi();
  const client = useQueryClient();
  const key = settingsKey(businessId, reportId);
  return useMutation({
    mutationFn: (update: TemplateSettingsUpdate) =>
      api
        .patch<{
          settings: TemplateSettings;
        }>(`${reportsPath(businessId, reportId)}/settings`, update)
        .then((res) => res.data.settings),
    onMutate: async (update) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<TemplateSettings>(key);
      if (previous) client.setQueryData(key, applyUpdate(previous, update));
      return { previous };
    },
    onError: (error, _update, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
      toast.error("The setting was not saved. Try again.", {
        description: reportError(error),
      });
    },
    onSuccess: (settings, update) => {
      client.setQueryData(key, settings);
      // The order is read live: it applies at once, and the Review reads it.
      const immediate = Object.keys(update).every(
        (name) =>
          name === "rule_order" ||
          name === "owners" ||
          name === "dismiss_request",
      );
      toast.success(immediate ? "Saved." : "Saved. Applies to future reports.");
      // The findings are paged in that order too: page one changes with it.
      if ("rule_order" in update) {
        void client.invalidateQueries({
          queryKey: [...reportKey(businessId), "overview", reportId],
        });
        void client.invalidateQueries({ queryKey: findingsKey(businessId) });
      }
      // The template's JSON carries the tolerance too.
      void client.invalidateQueries({
        queryKey: [...reportKey(businessId), "reports"],
      });
    },
  });
};
